/**
 * Handschutzplan-Modul · Baustein 6 + Nachbesserung (Aug 2026) — Plakat-Druckansicht.
 *
 * Interne SPA-Route `/druck/handschutzplan/:token`, wird AUSSCHLIESSLICH vom
 * Chromium-Renderer geladen (Token-autorisiert, anonym). Eigenständige
 * Komponente — WeltPlakat dient nur als Qualitäts-Referenz, null geteilte
 * Kurs-Logik. DIN-Geometrie über das generische `dinProfiles`-Fundament.
 *
 * Design-Anspruch (Betreiberentscheidung + Nachbesserung 27.08.2026):
 * besser als die BGW-Vorlagen — kräftiges Kopfband, farbkodierte Kategorien,
 * fotorealistisches Bild an JEDER Zeile (indexparallele `zeilen_bild_urls`
 * vom Druckdaten-Endpoint), Verbots-Zeilen mit rot akzentuiertem Foto,
 * Ablauf-Bildleisten (6-Schritt-Folgen) unterhalb der Tabelle VOR dem roten
 * Erste-Hilfe-Kasten. Saubere Seitenumbrüche: Zeilen, Leisten und der
 * Erste-Hilfe-Kasten sind unteilbar, der Tabellenkopf wiederholt sich.
 * KEIN Hinweis auf automatisierte Erzeugung (R8 gilt auch im PDF).
 */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { KATEGORIE_LABELS, formatDateDe } from "@/lib/handschutzplan";
import { getDinProfile } from "@/print/dinProfiles";

const KATEGORIE_FARBEN = {
  hautschutz: "#0e7490",
  handschuhe: "#b45309",
  haende_desinfizieren: "#7c3aed",
  haende_waschen: "#1d4ed8",
  haende_pflegen: "#15803d",
};
const VERBOT_FARBE = "#b91c1c";

function istVerbotsZeile(zeile) {
  return (zeile?.womit || "").trim().toLowerCase().startsWith("kein");
}

export default function HandschutzplanPlakat() {
  const { token } = useParams();
  const [daten, setDaten] = useState(null);
  const [fehler, setFehler] = useState(false);

  useEffect(() => {
    api
      .getHandschutzplanDruckDaten(token)
      .then(setDaten)
      .catch(() => setFehler(true));
  }, [token]);

  if (fehler) {
    return (
      <div data-testid="hsp-plakat-error" style={{ padding: 24, fontFamily: "sans-serif" }}>
        Druckkontext ungültig oder abgelaufen.
      </div>
    );
  }

  const profil = getDinProfile(daten?.format, daten?.orientation);
  const { typo } = profil;
  const content = daten?.content || {};
  const zeilen = content.zeilen || [];
  const ersteHilfe = content.erste_hilfe_hautkontakt || [];
  const zeilenBildUrls = daten?.zeilen_bild_urls || [];
  const ablauf = daten?.ablauf || [];
  const geladen = Boolean(daten);
  // Nachbesserung: deutlich größere Bildspalte (vorher 30/34 mm).
  const bildSpalteMm = Math.min(46, Math.round(profil.safeWidthMm * 0.2));

  return (
    <div
      data-testid="hsp-plakat-sheet"
      data-geladen={geladen ? "1" : "0"}
      style={{
        width: `${profil.widthMm}mm`,
        minHeight: `${profil.heightMm}mm`,
        margin: "0 auto",
        background: "#ffffff",
        color: "#0f172a",
        fontFamily:
          "'Inter','Segoe UI',system-ui,-apple-system,'Helvetica Neue',sans-serif",
        boxSizing: "border-box",
      }}
    >
      <style>{`
        @page { size: ${profil.cssPageSize}; margin: 0; }
        @media print { html, body { margin: 0 !important; padding: 0 !important; } }
        .hsp-zeile { page-break-inside: avoid; break-inside: avoid; }
        .hsp-eh { page-break-inside: avoid; break-inside: avoid; }
        .hsp-ablauf { page-break-inside: avoid; break-inside: avoid; }
        thead { display: table-header-group; }
      `}</style>

      {/* Kopfband */}
      <div
        style={{
          // CI-Blau seit 19.09.2026 (Betreiberentscheidung): Verlauf vom
          // Logo-Badge-Blau (#1a3f6f) zur Logo-Wortmarken-Farbe (#0f1f2e),
          // gleiche 3-Stufen-Abdunklung wie der frühere Teal-Verlauf.
          background: "linear-gradient(120deg, #1a3f6f 0%, #142f54 55%, #0f1f2e 100%)",
          color: "#ffffff",
          padding: `${profil.marginMm * 0.8}mm ${profil.marginMm}mm`,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-end",
          gap: "8mm",
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontSize: typo.small,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              opacity: 0.85,
              fontWeight: 600,
            }}
          >
            Hautschutz- und Handschutzplan
          </div>
          <div
            data-testid="hsp-plakat-titel"
            style={{ fontSize: typo.title, fontWeight: 800, lineHeight: 1.12, marginTop: "2mm" }}
          >
            {content.einsatzbereich_name || ""}
          </div>
          {daten?.company_name ? (
            <div style={{ fontSize: typo.body, opacity: 0.9, marginTop: "1.5mm" }}>
              {daten.company_name}
            </div>
          ) : null}
        </div>
        <div style={{ textAlign: "right", fontSize: typo.small, opacity: 0.9, flexShrink: 0 }}>
          {daten?.generated_at ? (
            <div>Stand: {formatDateDe(daten.generated_at)}</div>
          ) : null}
          {daten?.naechste_pruefung_faellig_am ? (
            <div>Nächste Prüfung: {formatDateDe(daten.naechste_pruefung_faellig_am)}</div>
          ) : null}
        </div>
      </div>

      {/* Tabelle */}
      <div style={{ padding: `${profil.marginMm * 0.7}mm ${profil.marginMm}mm` }}>
        <table
          data-testid="hsp-plakat-tabelle"
          style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}
        >
          <thead>
            <tr>
              <th style={{ width: `${bildSpalteMm + 2}mm` }} />
              {["Was", "Wann", "Womit", "Wie"].map((h, i) => (
                <th
                  key={h}
                  style={{
                    textAlign: "left",
                    fontSize: typo.h2,
                    fontWeight: 800,
                    color: "#0f1f2e",
                    borderBottom: "0.8mm solid #0f1f2e",
                    padding: "1.5mm 2mm 1.5mm 2mm",
                    width: i === 0 ? "22%" : undefined,
                  }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {zeilen.map((z, idx) => {
              const verbot = istVerbotsZeile(z);
              const farbe = verbot
                ? VERBOT_FARBE
                : KATEGORIE_FARBEN[z.kategorie] || "#334155";
              const bildUrl = zeilenBildUrls[idx] || "";
              return (
                <tr
                  key={idx}
                  className="hsp-zeile"
                  data-testid={`hsp-plakat-zeile-${idx}`}
                  style={{ borderBottom: "0.3mm solid #e2e8f0" }}
                >
                  <td style={{ padding: "2.5mm 2mm 2.5mm 0", verticalAlign: "top" }}>
                    {bildUrl ? (
                      <div style={{ width: `${bildSpalteMm}mm` }}>
                        <img
                          src={bildUrl}
                          alt=""
                          data-testid={`hsp-plakat-zeile-bild-${idx}`}
                          style={{
                            width: `${bildSpalteMm}mm`,
                            height: `${Math.round(bildSpalteMm * 0.72)}mm`,
                            objectFit: "cover",
                            borderRadius: "1.5mm",
                            border: `${verbot ? "0.8mm" : "0.5mm"} solid ${farbe}`,
                            display: "block",
                          }}
                        />
                        {verbot ? (
                          <div
                            style={{
                              marginTop: "1mm",
                              background: VERBOT_FARBE,
                              color: "#ffffff",
                              textAlign: "center",
                              fontSize: typo.small,
                              fontWeight: 800,
                              textTransform: "uppercase",
                              letterSpacing: "0.12em",
                              borderRadius: "1mm",
                              padding: "0.6mm 0",
                            }}
                          >
                            Verbot
                          </div>
                        ) : null}
                      </div>
                    ) : (
                      <div
                        style={{
                          width: `${bildSpalteMm}mm`,
                          height: verbot ? "auto" : `${Math.round(bildSpalteMm * 0.72)}mm`,
                          minHeight: verbot ? `${Math.round(bildSpalteMm * 0.4)}mm` : undefined,
                          borderRadius: "1.5mm",
                          background: verbot ? "#fef2f2" : "#f1f5f9",
                          border: `0.5mm solid ${verbot ? VERBOT_FARBE : "#e2e8f0"}`,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: verbot ? VERBOT_FARBE : "#94a3b8",
                          fontSize: typo.small,
                          fontWeight: 800,
                          textTransform: "uppercase",
                          letterSpacing: "0.08em",
                        }}
                      >
                        {verbot ? "Verbot" : ""}
                      </div>
                    )}
                  </td>
                  <td style={{ padding: "2mm", verticalAlign: "top", overflowWrap: "break-word" }}>
                    <div
                      style={{
                        fontSize: typo.small,
                        fontWeight: 800,
                        textTransform: "uppercase",
                        letterSpacing: "0.1em",
                        color: farbe,
                      }}
                    >
                      {KATEGORIE_LABELS[z.kategorie] || z.kategorie}
                    </div>
                    <div style={{ fontSize: typo.body, fontWeight: 700, marginTop: "1mm" }}>
                      {z.was}
                    </div>
                  </td>
                  <td style={{ padding: "2mm", verticalAlign: "top", fontSize: typo.body, overflowWrap: "break-word" }}>
                    {z.wann}
                  </td>
                  <td style={{ padding: "2mm", verticalAlign: "top", fontSize: typo.body, overflowWrap: "break-word" }}>
                    <span style={verbot ? { color: VERBOT_FARBE, fontWeight: 700 } : undefined}>
                      {z.womit}
                    </span>
                  </td>
                  <td style={{ padding: "2mm", verticalAlign: "top", fontSize: typo.body, overflowWrap: "break-word" }}>
                    {z.wie}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {/* Ablauf-Bildleisten (Nachbesserung 3a): 6-Schritt-Folgen, unteilbar,
            zwischen Tabelle und Erste-Hilfe-Kasten (Lesefluss Übersicht →
            Anleitung → Notfall). Fehlende Schrittbilder = Platzhalter-Kachel,
            die Leiste bleibt komplett (fail-open). */}
        {ablauf.map((a) => {
          const farbe = KATEGORIE_FARBEN[a.kategorie] || "#334155";
          return (
            <div
              key={a.kategorie}
              className="hsp-ablauf"
              data-testid={`hsp-plakat-ablauf-${a.kategorie}`}
              style={{
                marginTop: "5mm",
                border: `0.4mm solid ${farbe}`,
                borderRadius: "2mm",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  background: farbe,
                  color: "#ffffff",
                  fontSize: typo.h2,
                  fontWeight: 800,
                  padding: "1.8mm 4mm",
                }}
              >
                {a.titel}
              </div>
              <div style={{ display: "flex", gap: "2mm", padding: "2.5mm 3mm" }}>
                {(a.schritte || []).map((s) => (
                  <div key={s.nr} style={{ flex: "1 1 0", minWidth: 0 }}>
                    <div style={{ position: "relative" }}>
                      {s.url ? (
                        <img
                          src={s.url}
                          alt=""
                          data-testid={`hsp-plakat-ablauf-bild-${a.kategorie}-${s.nr}`}
                          style={{
                            width: "100%",
                            aspectRatio: "4 / 3",
                            objectFit: "cover",
                            borderRadius: "1.2mm",
                            border: "0.3mm solid #e2e8f0",
                            display: "block",
                          }}
                        />
                      ) : (
                        <div
                          style={{
                            width: "100%",
                            aspectRatio: "4 / 3",
                            borderRadius: "1.2mm",
                            background: "#f1f5f9",
                            border: "0.3mm solid #e2e8f0",
                          }}
                        />
                      )}
                      <div
                        style={{
                          position: "absolute",
                          top: "1mm",
                          left: "1mm",
                          width: "5.5mm",
                          height: "5.5mm",
                          borderRadius: "50%",
                          background: farbe,
                          color: "#ffffff",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: typo.small,
                          fontWeight: 800,
                        }}
                      >
                        {s.nr}
                      </div>
                    </div>
                    <div
                      style={{
                        marginTop: "1.2mm",
                        fontSize: typo.small,
                        fontWeight: 600,
                        lineHeight: 1.3,
                        color: "#0f172a",
                        textAlign: "center",
                      }}
                    >
                      {s.kurz}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}

        {/* Erste Hilfe */}
        {ersteHilfe.length > 0 && (
          <div
            className="hsp-eh"
            data-testid="hsp-plakat-erste-hilfe"
            style={{
              marginTop: "6mm",
              border: `0.6mm solid ${VERBOT_FARBE}`,
              borderRadius: "2mm",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                background: VERBOT_FARBE,
                color: "#ffffff",
                fontSize: typo.h2,
                fontWeight: 800,
                padding: "2mm 4mm",
              }}
            >
              Erste Hilfe bei Hautkontakt
            </div>
            <div style={{ padding: "3mm 4mm", background: "#fef2f2" }}>
              {ersteHilfe.map((e, idx) => (
                <div
                  key={idx}
                  style={{
                    fontSize: typo.body,
                    color: "#7f1d1d",
                    padding: "1mm 0",
                    display: "flex",
                    gap: "2mm",
                  }}
                >
                  <span style={{ fontWeight: 800, flexShrink: 0 }}>{e.produkt}:</span>
                  <span>{e.massnahme}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
